'use client';

import { useId, useRef, useState } from 'react';

import { motion, useInView, useReducedMotion } from 'framer-motion';
import { Check, Pause, Play } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import type { MarketingLocale } from '~/lib/marketing/site';

import { PLATFORM_SECTION, PLATFORM_TABS } from '../../_lib/platform.content';
import { DEMOS, ProductDemo } from '../product-demos/demo-registry';

const TAB_SECONDS = 10;
const EASE = [0.16, 1, 0.3, 1] as const;

/**
 * Capability tour: a tab per product surface, each with a live demo.
 * Auto-advances while on screen (CSS-driven progress bar, so pausing is a
 * play-state flip and advancing is an animationend event). Any tab click
 * hands control to the visitor and stops the tour; the pause button toggles
 * it explicitly (WCAG 2.2.2).
 */
export function PlatformShowcase({ locale }: { locale: MarketingLocale }) {
  const isRTL = locale === 'ar';
  const baseId = useId();
  const sectionRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const inView = useInView(sectionRef, { amount: 0.3 });
  const reducedMotion = useReducedMotion() ?? false;
  // `switched` gates the panel entrance: the first panel must paint straight
  // from the server HTML, not wait for hydration at opacity 0.
  const [state, setState] = useState({ active: 0, playing: true, switched: false });

  const running = state.playing && inView && !reducedMotion;

  const tab = PLATFORM_TABS[state.active]!;
  const isPhone = DEMOS[tab.id].kind === 'phone';

  const select = (index: number, { focus = false } = {}) => {
    const next = (index + PLATFORM_TABS.length) % PLATFORM_TABS.length;
    setState({ active: next, playing: false, switched: true });

    if (focus) {
      tabRefs.current[next]?.focus();
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const forward = isRTL ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRTL ? 'ArrowRight' : 'ArrowLeft';

    if (event.key === forward || event.key === 'ArrowDown') {
      event.preventDefault();
      select(state.active + 1, { focus: true });
    } else if (event.key === backward || event.key === 'ArrowUp') {
      event.preventDefault();
      select(state.active - 1, { focus: true });
    } else if (event.key === 'Home') {
      event.preventDefault();
      select(0, { focus: true });
    } else if (event.key === 'End') {
      event.preventDefault();
      select(PLATFORM_TABS.length - 1, { focus: true });
    }
  };

  const advance = () =>
    setState((prev) => ({
      ...prev,
      switched: true,
      active: (prev.active + 1) % PLATFORM_TABS.length,
    }));

  return (
    <section
      id="platform"
      ref={sectionRef}
      className="scroll-mt-16 bg-white px-4 py-16 md:px-8 md:py-24 lg:px-[90px] lg:py-[120px]"
    >
      <div className="mx-auto max-w-[1320px]">
        <div className="max-w-[760px]">
          <div className="mb-4 flex items-center gap-3 lg:mb-6">
            <span className="h-2 w-2 rounded-full bg-[#202020]" aria-hidden="true" />
            <p className="font-roboto-mono text-[12px] uppercase tracking-wide text-[#202020] md:text-[14px]">
              {PLATFORM_SECTION.label[locale]}
            </p>
          </div>
          <h2
            className="font-roboto text-[30px] leading-[1.12] text-[#202020] md:text-[40px] lg:text-[48px]"
            style={{ fontVariationSettings: "'wdth' 100" }}
          >
            {PLATFORM_SECTION.heading[locale]}
          </h2>
          <p className="mt-4 font-roboto text-[16px] leading-relaxed text-[#2b2b2b]/70 md:text-[18px]">
            {PLATFORM_SECTION.description[locale]}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:mt-14 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-10">
          <div className="flex items-start gap-2 lg:flex-col lg:gap-4">
            <div
              role="tablist"
              aria-label={PLATFORM_SECTION.label[locale]}
              onKeyDown={onKeyDown}
              className="-ms-4 flex min-w-0 flex-1 gap-2 overflow-x-auto pb-1 pe-2 ps-4 [scrollbar-width:none] lg:ms-0 lg:w-full lg:flex-none lg:flex-col lg:overflow-visible lg:pe-0 lg:ps-0"
            >
              {PLATFORM_TABS.map((item, index) => {
                const selected = index === state.active;

                return (
                  <button
                    key={item.id}
                    ref={(node) => {
                      tabRefs.current[index] = node;
                    }}
                    type="button"
                    role="tab"
                    id={`${baseId}-tab-${index}`}
                    aria-selected={selected}
                    aria-controls={`${baseId}-panel`}
                    tabIndex={selected ? 0 : -1}
                    onClick={() => select(index)}
                    className={cn(
                      'relative shrink-0 overflow-hidden whitespace-nowrap rounded-full border px-4 py-2.5 text-start transition-colors lg:whitespace-normal lg:rounded-[16px] lg:px-5 lg:py-4',
                      selected
                        ? 'border-[#202020] bg-[#202020] text-white lg:border-[#dcd7cf] lg:bg-[#f7f6f4] lg:text-[#202020]'
                        : 'border-[#dcd7cf] text-[#2b2b2b]/70 hover:border-[#202020]/30 hover:text-[#202020] lg:border-transparent',
                    )}
                  >
                    <span className="flex items-center gap-3">
                      <span className="hidden font-roboto-mono text-[11px] text-[#8a857c] lg:inline">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="font-roboto text-[14px] font-medium lg:text-[17px]">
                        {item.label[locale]}
                      </span>
                    </span>
                    {selected && (
                      <span className="mt-1.5 hidden ps-[30px] font-roboto text-[13px] leading-snug text-[#2b2b2b]/60 lg:block">
                        {item.heading[locale]}
                      </span>
                    )}
                    {selected && !reducedMotion && state.playing && (
                      <span
                        key={`progress-${state.active}`}
                        aria-hidden="true"
                        onAnimationEnd={advance}
                        className="flow-tab-progress absolute inset-x-0 bottom-0 block h-[2px] bg-white/60 lg:bg-[#202020]"
                        style={{
                          animationDuration: `${TAB_SECONDS}s`,
                          animationPlayState: running ? 'running' : 'paused',
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {!reducedMotion && (
              <button
                type="button"
                onClick={() => setState((prev) => ({ ...prev, playing: !prev.playing }))}
                aria-label={
                  state.playing ? PLATFORM_SECTION.pause[locale] : PLATFORM_SECTION.play[locale]
                }
                className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#dcd7cf] text-[#202020] transition-colors hover:bg-[#f7f6f4] lg:ms-5 lg:h-auto lg:w-auto lg:gap-2 lg:border-0 lg:px-0 lg:hover:bg-transparent"
              >
                {state.playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                <span className="hidden font-roboto-mono text-[11px] uppercase tracking-wide text-[#8a857c] lg:inline">
                  {state.playing ? PLATFORM_SECTION.pause[locale] : PLATFORM_SECTION.play[locale]}
                </span>
              </button>
            )}
          </div>

          <div
            role="tabpanel"
            id={`${baseId}-panel`}
            aria-labelledby={`${baseId}-tab-${state.active}`}
            className="relative overflow-hidden rounded-[28px] bg-[#ede8e2] lg:min-h-[700px]"
          >
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.7),transparent_60%)]"
            />
            <motion.div
              key={tab.id}
              initial={state.switched ? { opacity: 0, y: 18 } : false}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className={cn(
                'relative grid gap-8 p-6 md:p-10',
                isPhone && 'md:grid-cols-[minmax(0,1fr)_290px] md:items-center lg:gap-12 lg:p-12',
              )}
            >
              <div className={cn(!isPhone && 'max-w-[720px]')}>
                <h3
                  className="font-roboto text-[24px] leading-tight text-[#202020] md:text-[30px]"
                  style={{ fontVariationSettings: "'wdth' 100" }}
                >
                  {tab.heading[locale]}
                </h3>
                <p className="mt-3 font-roboto text-[15px] leading-relaxed text-[#2b2b2b]/75 md:text-[17px]">
                  {tab.description[locale]}
                </p>
                <ul
                  className={cn(
                    'mt-5 grid gap-2.5',
                    !isPhone && 'md:grid-cols-3 md:gap-4',
                  )}
                >
                  {tab.points.map((point) => (
                    <li key={point.en} className="flex items-start gap-2.5">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#202020] text-white">
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className="font-roboto text-[14px] leading-snug text-[#202020] md:text-[15px]">
                        {point[locale]}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className={cn('mx-auto w-full', isPhone ? 'max-w-[290px]' : 'max-w-[800px]')}>
                <ProductDemo demo={tab.id} locale={locale} maxScale={isPhone ? 1 : 1.05} />
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </section>
  );
}
